import moment from 'moment/moment';
import { computed, ref, watch } from 'vue';
import emitter from '../mitt';
import { useRouteDataStore } from '../stores/route-data';
import { ReactiveModel } from './reactive-model';

export class Order extends ReactiveModel {
  id;
  user;
  delivery_type;
  delivery_time_from;
  serving_time;
  expected_delivery_time;
  status;
  comments;
  extra_payment_required;
  wc_payment_url;
  total_quantity;
  product;
  box_count;
  delivery_address;
  delivery_zone_id;
  route_code;
  tour_code;
  vehicle_id;
  driver_id;
  order_products;
  reference_code;
  safeTransportTimeErrors;
  capacityCategoryErrors;
  setSafeTransportTimeLimitErrors;
  setCapacityCategoryErrorsErrors;
  
  // Manual stop 
  isManualStop;
  manualStopAddress;
  manualStopDescription;
  
  constructor(obj = null) {
    super(obj);
    if (obj) {
      this.id = obj.id;
      this.delivery_type = obj.delivery_type ?? null;
      this.delivery_time_from = obj.delivery_time_from ?? null;
      this.serving_time = obj.serving_time ?? null;
      this.expected_delivery_time = obj.expected_delivery_time ?? null;
      this.status = obj.status ?? null;
      this.comments = obj.comments ?? [];
      this.extra_payment_required = obj['extra_payment_required?'];
      this.wc_payment_url = obj.wc_payment_url ?? null;
      this.total_quantity = obj.total_quantity ?? null;
      this.product = obj.product ?? null;
      this.box_count = obj.box_count ?? null;
      this.delivery_address = obj.delivery_address ?? null;
      this.delivery_zone_id = obj.delivery_zone_id ?? null;
      this.route_code = obj.route_code ?? null;
      this.tour_code = obj.tour_code ?? null;
      this.vehicle_id = obj.vehicle_id ?? null;
      this.driver_id = obj.driver_id ?? null;
      this.order_products = obj.order_products ?? [];
      this.reference_code = obj.reference_code ?? null;
      this.user = obj.user ?? null;
      this.safeTransportTimeErrors = {
        yellow: 0,
        red: 0,
        green: 0,
      };
      this.capacityCategoryErrors = {
        yellow: 0,
        red: 0,
        green: 0,
      };
      this.isManualStop = obj.isManualStop;
      if (this.isManualStop) { 
        this.status = "Manuel stop";
        this.errorOverviewCounts = {
              red: 0,
              yellow: 0,
              green: 0,
        };
        this.total_quantity = 0;
        this.box_count = 0;
        this.serving_time = null;
        this.comments = [];
      }
      this.manualStopAddress = obj.manualStopAddress;
      this.manualStopDescription = obj.manualStopDescription;
    }
  }

  setup() {
    super.setup();
    const routeDataStore = useRouteDataStore();
    this.lastComment = this.comments.length > 0 ? this.comments[this.comments.length - 1].comment : null;
    this.paymentMissing = this.extra_payment_required && this.wc_payment_url
    this.url = computed(() => {
      return `/order_management/order_details/${this.id}`;
    });
    this.fullAddress = computed(() => {
      if (this.delivery_address) {
        return this.delivery_address.address_1 + ', ' + this.delivery_address.zipcode + ' ' + this.delivery_address.city;
      } else {
        return '';
      }
    });
    this.area = computed(() => {
      if (this.delivery_address) {
        return this.delivery_address.zipcode + ' ' + this.delivery_address.city;
      } else {
        return '';
      }
    });
    this.deliveryIntervalStart = computed(() => {
      if (this.isManualStop) {
        return null;
      }
      if (this.delivery_type == 'cold') {
        return moment(this.delivery_time_from.replace(/Z.*/, '')).format('HH:mm');
      } else {
        return moment(this.serving_time.replace(/Z.*/, '')).format('HH:mm');
      }
    });
    this.deliveryIntervalEnd = computed(() => {
      if (this.isManualStop) {
        return null;
      }
      if (this.delivery_type == 'cold') {
        return moment(this.delivery_time_from.replace(/Z.*/, '')).add(2, 'hour').format('HH:mm');
      } else {
        return moment(this.serving_time.replace(/Z.*/, '')).add(30, 'minutes').format('HH:mm');
      }
    });
    this.deliveryDateTime = computed(() => {
      if (this.isManualStop) {
        return '';
      }
      return this.deliveryIntervalStart + ' - ' + this.deliveryIntervalEnd;
    });

    this.prevTourCode = this.tour_code;
    watch(() => this.tour_code, (newValue, oldValue) => {
      if (newValue !== oldValue) {
        setTimeout(() => {
          this.prevTourCode = newValue;
        }, 25);
      }
    });

    this.prevRouteCode = this.tour_code;
    watch(() => this.route_code, (newValue, oldValue) => {
      if (newValue !== oldValue) {
        setTimeout(() => {
          this.prevRouteCode = newValue;
        }, 25);
      }
    });

    const translate = (key) => {
      const translations = {
        // delivery_type
        'cold': 'Kold',
        'warm': 'Varm',
        // status
        'requested': 'Indsendt',
        'approved': 'Godkendt',
        'locked': 'Låst',
        'payment_received': 'Restbetaling modtaget',
        'rejected': 'Afvist',
        'note': 'Note',
        'delivered': 'Leveret',
      };
      if (translations[key]) {
        return translations[key];
      }
      return key;
    };

    this.deliveryTypeTitle = computed(() => {
      return translate(this.delivery_type);
    });

    this.statusTitle = computed(() => {
      return translate(this.status);
    });

    this.productTitle = computed(() => {
      return this.product && this.product.name;
    });

    let tempDriveStart = ref(null);
    this.driveStart = computed({
      get: () => {
        // console.log('driveStart get', this.driveEnd, this.driveDuration);
        if (this.driveEnd && this.driveDuration !== null) {
          if (tempDriveStart.value !== null) {
            console.log('reset tempDriveStart');
            tempDriveStart.value = null;
          }
          return moment(this.driveEnd, 'HH:mm').subtract(this.driveDuration, 'minutes').format('HH:mm');
        } else if (tempDriveStart.value !== null) {
          return tempDriveStart.value;
        } else {
          return null;
        }
      },
      set: (newValue) => {
        // console.log('driveStart set', newValue);
        const oldStartValue = this.driveStart;
        if (this.driveStart && newValue && this.driveEnd && this.driveDuration !== null) {
          // if all is filled - push the delivery start
          const diff = this.driveStart ? moment(newValue, 'HH:mm').diff(moment(this.driveStart, 'HH:mm'), 'minutes') : 0;
          this.deliveryStart = moment(this.expected_delivery_time.replace(/Z.*/, '')).add(diff, 'minutes').format('HH:mm');
        } else if (!this.driveEnd && newValue && this.driveDuration !== null) {
          // drive end can be calculated from drive start and duration
          this.driveEnd = moment(newValue, 'HH:mm').add(this.driveDuration, 'minutes').format('HH:mm');
        } else if (this.driveEnd && newValue && this.driveDuration === null) {
          // drive duration can be calculated from drive start and end
          this.driveDuration = Math.max(0, moment(this.driveEnd, 'HH:mm').diff(moment(newValue, 'HH:mm'), 'minutes'));
        } else {
          // if the link to delivery start is broken, save it in a temp variable
          tempDriveStart.value = newValue ? newValue : null;
        }
      }
    });

    this.driveDuration = computed({
      get: () => {
        if (Object.hasOwn(routeDataStore.routeData.drive_durations, this.id)) {
          return routeDataStore.routeData.drive_durations[this.id];
        } else {
          return null;
        }
      },
      set: (newValue) => {
        // console.log('driveDuration set', newValue, tempDriveStart.value);
        if (this.driveDuration !== null && newValue !== null && this.driveEnd) {
          // if all is filled - push the delivery start
          const diff = newValue - this.driveDuration;
          routeDataStore.routeData.drive_durations[this.id] = newValue;
          this.deliveryStart = moment(this.driveEnd, 'HH:mm').add(diff, 'minutes').format('HH:mm');
        } else if (newValue !== null && this.driveStart && !this.driveEnd) {
          // drive end can be calculated from drive start and duration
          routeDataStore.routeData.drive_durations[this.id] = newValue;
          this.driveEnd = moment(this.driveStart, 'HH:mm').add(newValue, 'minutes').format('HH:mm');
        } else {
          const oldStartValue = this.driveStart;
          routeDataStore.routeData.drive_durations[this.id] = newValue;
          this.startChanged(this.driveStart, oldStartValue);
        }
      }
    });

    this.driveEnd = computed({
      get: () => {
        if (this.deliveryStart) {
          return moment(this.deliveryStart, 'HH:mm').format('HH:mm');
        } else {
          return null;
        }
      },
      set: (newValue) => {
        // console.log('driveEnd set', newValue);
        if (this.driveStart && newValue && this.driveDuration === null) {
          // drive duration can be calculated from drive start and end
          this.driveDuration = Math.max(0, moment(newValue, 'HH:mm').diff(moment(this.driveStart, 'HH:mm'), 'minutes'));
        }
        this.deliveryStart = newValue;
      }
    });

    this.deliveryStart = computed({
      get: () => {
        if (this.expected_delivery_time) {
          return moment(this.expected_delivery_time.replace(/Z.*/, '')).format('HH:mm');
        } else {
          return null;
        }
      },
      set: (newValue) => {
        if (newValue) {
          const startWasTemp = tempDriveStart.value !== null;
          const endWasTemp = this.deliveryEnd === null;
          if (this.driveStart && this.driveDuration === null) {
            // drive duration can be calculated from drive start and end
            routeDataStore.routeData.drive_durations[this.id] = Math.max(0, moment(newValue, 'HH:mm').diff(moment(this.driveStart, 'HH:mm'), 'minutes'));
          }
          if (this.deliveryEnd && this.deliveryDuration === null) {
            // delivery duration can be calculated from delivery start and end
            routeDataStore.routeData.service_durations[this.id] = Math.max(0, moment(this.deliveryEnd, 'HH:mm').diff(moment(newValue, 'HH:mm'), 'minutes'));
          }
          const oldStartValue = this.driveStart;
          const oldEndValue = this.deliveryEnd;
          const diff = this.driveEnd ? moment(newValue, 'HH:mm').diff(moment(this.driveEnd, 'HH:mm'), 'minutes') : 0;
          // console.log('expected_delivery_time', newValue, diff, this.expected_delivery_time ? moment(this.expected_delivery_time.replace(/Z.*/, '')).add(diff, 'minutes').format() : moment(newValue, 'HH:mm').format());
          this.expected_delivery_time = this.expected_delivery_time ? moment(this.expected_delivery_time.replace(/Z.*/, '')).add(diff, 'minutes').format() : moment(newValue, 'HH:mm').format();
          this.startChanged(this.driveStart, oldStartValue, startWasTemp);
          this.endChanged(this.deliveryEnd, oldEndValue, endWasTemp);
        } else {
          this.expected_delivery_time = null;
        }
      }
    });

    this.deliveryDuration = computed({
      get: () => {
        if (Object.hasOwn(routeDataStore.routeData.service_durations, this.id)) {
          return routeDataStore.routeData.service_durations[this.id];
        } else {
          return null;
        }
      },
      set: (newValue) => {
        // console.log('deliveryDuration set', newValue);
        const oldEndValue = this.deliveryEnd;
        if (this.deliveryDuration !== null && newValue !== null && this.deliveryStart && this.deliveryEnd) {
          // if all is filled - push the delivery end
          const diff = newValue - this.deliveryDuration;
          routeDataStore.routeData.service_durations[this.id] = newValue;
          this.deliveryEnd = moment(this.deliveryEnd, 'HH:mm').add(diff, 'minutes').format('HH:mm');
        } else if (newValue !== null && !this.deliveryStart && this.deliveryEnd) {
          // delivery start can be calculated from delivery end and duration
          routeDataStore.routeData.service_durations[this.id] = newValue;
          this.deliveryStart = moment(this.deliveryEnd, 'HH:mm').subtract(newValue, 'minutes').format('HH:mm');
        } else {
          routeDataStore.routeData.service_durations[this.id] = newValue;
        }
      }
    });

    const tempDeliveryEnd = ref(null);
    this.deliveryEnd = computed({
      get: () => {
        if (this.deliveryStart && this.deliveryDuration !== null) {
          if (tempDeliveryEnd.value !== null) {
            console.log('reset tempDeliveryEnd');
            tempDeliveryEnd.value = null;
          }
          return moment(this.deliveryStart, 'HH:mm').add(this.deliveryDuration, 'minutes').format('HH:mm');
        } else if (tempDeliveryEnd.value !== null) {
          return tempDeliveryEnd.value;
        } else {
          return null;
        }
      },
      set: (newValue) => {
        if (this.deliveryEnd && newValue && this.deliveryStart && this.deliveryDuration !== null) {
          // if all is filled
          if (emitEnd) {
            // if the changes are made to this order - push the delivery end
            this.endChanged(newValue, this.deliveryEnd);
          } else {
            // if the order is being pushed by changes in other orders - push the delivery start
            const diff = moment(newValue, 'HH:mm').diff(moment(this.deliveryEnd, 'HH:mm'), 'minutes');
            this.deliveryStart = moment(this.deliveryStart, 'HH:mm').add(diff, 'minutes').format('HH:mm');
          }
        } else if (!this.deliveryStart && newValue && this.deliveryDuration !== null) {
          // delivery start can be calculated from delivery end and duration
          this.deliveryStart = moment(newValue, 'HH:mm').subtract(this.deliveryDuration, 'minutes').format('HH:mm');
        } else if (this.deliveryStart && newValue && this.deliveryDuration === null) {
          // delivery duration can be calculated from delivery start and end
          const oldEndValue = this.deliveryEnd;
          this.deliveryDuration = Math.max(0, moment(newValue, 'HH:mm').diff(moment(this.deliveryStart, 'HH:mm'), 'minutes'));
          this.endChanged(newValue, oldEndValue);
        } else {
          // if the link to delivery start is broken, save it in a temp variable
          tempDeliveryEnd.value = newValue ? newValue : null;
        }
      }
    });

    this.clearTempValues = () => {
      tempDriveStart.value = null;
      tempDeliveryEnd.value = null;
    }

    this.startChanged = (newValue, oldValue, ignoreNoChangeInValue = false) => {
      // console.log('startChanged', newValue, oldValue);
      if (!emitStart) {
        // console.log('startChanged return emitStart false');
        return;
      }
      if (newValue === oldValue && !ignoreNoChangeInValue) {
        // console.log('startChanged return newValue === oldValue');
        return;
      }
      emitter.emit('order-move-up', { id: this.id, trip_name: this.route_code, value: newValue, prev_value: oldValue });
    }

    this.endChanged = (newValue, oldValue, ignoreNoChangeInValue = false) => {
      // console.log('endChanged', newValue, oldValue);
      if (!emitEnd) {
        // console.log('endChanged return emitEnd false');
        return;
      }
      if (newValue === oldValue && !ignoreNoChangeInValue) {
        // console.log('endChanged return newValue === oldValue');
        return;
      }
      emitter.emit('order-move-down', { id: this.id, trip_name: this.route_code, value: newValue, prev_value: oldValue });
    }

    // assumes this is the first order on the trup
    this.tripStart = computed({
      get: () => {
        const loadDuration = routeDataStore.routeData.service_durations[`${this.route_code}_start`] ?? 10;
        if (this.driveStart && loadDuration !== null) {
          return moment(this.driveStart, 'HH:mm').subtract(loadDuration, 'minutes').format('HH:mm');
        } else {
          return null;
        }
      },
      set: (newValue) => {
        const oldStartValue = this.tripStart;
        const oldEndValue = this.deliveryEnd;
        const diff = this.tripStart ? moment(newValue, 'HH:mm').diff(moment(this.tripStart, 'HH:mm'), 'minutes') : 0;
        this.expected_delivery_time = moment(this.expected_delivery_time.replace(/Z.*/, '')).add(diff, 'minutes').format();
        this.startChanged(this.tripStart, oldStartValue);
        this.endChanged(this.deliveryEnd, oldEndValue);
      }
    });

    // assumes this is the last order on the trup
    this.tripEnd = computed({
      get: () => {
        const driveHomeDuration = routeDataStore.routeData.drive_durations[`${this.route_code}_end`] ?? null;
        const unloadDuration = routeDataStore.routeData.service_durations[`${this.route_code}_end`] ?? 10;
        const breakDuration = routeDataStore.routeData.service_durations[`${this.route_code}_break`] ?? null;
        if (this.deliveryEnd && driveHomeDuration !== null && unloadDuration !== null && breakDuration !== null) {
          return moment(this.deliveryEnd, 'HH:mm').add(driveHomeDuration + unloadDuration + breakDuration, 'minutes').format('HH:mm');
        } else {
          return null;
        }
      },
      set: (newValue) => {
        const oldStartValue = this.tripStart;
        const oldEndValue = this.deliveryEnd;
        const diff = this.tripStart ? moment(newValue, 'HH:mm').diff(moment(this.tripStart, 'HH:mm'), 'minutes') : 0;
        this.expected_delivery_time = moment(this.expected_delivery_time.replace(/Z.*/, '')).add(diff, 'minutes').format();
        this.startChanged(this.tripStart, oldStartValue);
        this.endChanged(this.deliveryEnd, oldEndValue);
      }
    });

    const yellowLimit = computed(() => {
      if (this.delivery_type === 'warm') {
        return window.settings.deliver_page_interval_warm_warning;
      } else {
        return window.settings.deliver_page_interval_cold_warning;
      }
    });

    const redLimit = computed(() => {
      if (this.delivery_type === 'warm') {
        return window.settings.deliver_page_interval_warm_error;
      } else {
        return window.settings.deliver_page_interval_cold_error;
      }
    });


    this.intervalIndicatorLevel = computed(() => {
      const deliveryTime = moment(this.deliveryStart, 'HH:mm');
      const intervalStart = moment(this.deliveryIntervalStart, 'HH:mm');
      const intervalEnd = moment(this.deliveryIntervalEnd, 'HH:mm');
      if (this.deliveryStart === null || this.route_code === null || this.tour_code === null) {
        return null;
      }
      if (deliveryTime < intervalStart || deliveryTime > intervalEnd) {
        return 'red';
      }
      const minDiff = Math.min(
        deliveryTime.diff(intervalStart, 'minutes'),
        intervalEnd.diff(deliveryTime, 'minutes'),
      );
      if (minDiff < redLimit.value) { 
        return 'red';
      }
      if (minDiff < yellowLimit.value) { 
        return 'yellow';
      } 
      return 'green';
    });

    this.errorOverviewCounts = computed(() => {
      const errorColorsCount = {
        red: 0,
        yellow: 0,
        green: 0,
      }
      if (this.deliveryStart === null || this.route_code === null || this.tour_code === null) {
        return errorColorsCount;
      }
      
      const deliveryTime = moment(this.deliveryStart, 'HH:mm');
      const intervalStart = moment(this.deliveryIntervalStart, 'HH:mm');
      const intervalEnd = moment(this.deliveryIntervalEnd, 'HH:mm');

      // check if the order is outside the interval
      if (deliveryTime < intervalStart || deliveryTime > intervalEnd) {
        errorColorsCount.red += 1;
      } else {
        const minDiff = Math.min(
          deliveryTime.diff(intervalStart, 'minutes'),
          intervalEnd.diff(deliveryTime, 'minutes'),
        );

        // max one error for inside intervel level
        if (minDiff < redLimit.value) {
          errorColorsCount.red += 1;
        } else if (minDiff < yellowLimit.value) {
          errorColorsCount.yellow += 1;
        } else {
          errorColorsCount.green += 1;
        }
      }


      Object.keys(errorColorsCount).forEach((color) => {
        errorColorsCount[color] += this.safeTransportTimeErrors[color];
        errorColorsCount[color] += this.capacityCategoryErrors[color];
      });

      return errorColorsCount;
    });
    
    this.setSafeTransportTimeLimitErrors = (color, count) => {
      this.safeTransportTimeErrors.green = color === 'green' ? count : 0;
      this.safeTransportTimeErrors.yellow = color === 'yellow' ? count : 0;
      this.safeTransportTimeErrors.red = color === 'red' ? count : 0;
    }
    
    this.setCapacityCategoryErrorsErrors = (color, count) => {
      this.capacityCategoryErrors.green = color === 'green' ? count : 0;
      this.capacityCategoryErrors.yellow = color === 'yellow' ? count : 0;
      this.capacityCategoryErrors.red = color === 'red' ? count : 0;
    }

    let emitStart = true;
    let emitEnd = true;
    this.moveOrder = (minutes, direction) => {
      console.log('moveOrder', minutes);
      if (direction === 'down') {
        emitStart = false;
        if (this.driveStart) {
          this.driveStart = moment(this.driveStart, 'HH:mm').add(minutes, 'minutes').format('HH:mm');
        }
        emitStart = true;
      } else if (direction === 'up') {
        emitEnd = false;
        if (this.deliveryEnd) {
          this.deliveryEnd = moment(this.deliveryEnd, 'HH:mm').add(minutes, 'minutes').format('HH:mm');
        }
        emitEnd = true;
      }
    }
  }
}